import { LEGACY_FARBEN, zerlege } from "@gui-creator/gemeinsam";
import { Bold, Braces, Italic, Palette, Strikethrough, Underline } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useSpielschrift } from "../inventar/schrift.ts";
import { SpielText } from "../inventar/SpielText.tsx";
import { MenueListe, type MenuePunkt } from "../ui/Menue.tsx";

export type PlatzhalterInfo = { name: string; beschreibung?: string | undefined; mehrzeilig?: boolean | undefined };

type Props = {
  label: string;
  wert: string;
  onWert: (neu: string) => void;
  rollen: Record<string, string>;
  mehrzeilig?: boolean;
  platzhalter?: PlatzhalterInfo[];
  vorschau?: "item" | "lore" | "titel" | null;
  kursivStandard?: boolean;
  hinweis?: string;
  leer?: string;
  deaktiviert?: boolean;
};

const WARTEN_MS = 300;

/** MiniMessage-Text mit Werkzeugen; Änderungen gehen nach kurzer Pause oder beim Verlassen raus. */
export function TextFeld({ label, wert, onWert, rollen, mehrzeilig, platzhalter = [], vorschau = null, kursivStandard = false, hinweis, leer, deaktiviert }: Props) {
  const id = useId();
  const feld = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [lokal, setLokal] = useState(wert);
  const [menue, setMenue] = useState<{ x: number; y: number; punkte: MenuePunkt[] } | null>(null);
  const zeit = useRef<number | undefined>(undefined);
  const zuletzt = useRef(wert);
  const glyphen = useSpielschrift();

  useEffect(() => {
    if (document.activeElement !== feld.current && wert !== zuletzt.current) {
      setLokal(wert);
      zuletzt.current = wert;
    }
  }, [wert]);

  const absenden = (t: string) => {
    window.clearTimeout(zeit.current);
    if (t !== zuletzt.current) {
      zuletzt.current = t;
      onWert(t);
    }
  };

  const aendern = (t: string) => {
    setLokal(t);
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => absenden(t), WARTEN_MS);
  };

  const einsetzen = (vor: string, nach = "") => {
    const el = feld.current;
    if (!el) return;
    const a = el.selectionStart ?? lokal.length;
    const b = el.selectionEnd ?? lokal.length;
    const neu = lokal.slice(0, a) + vor + lokal.slice(a, b) + nach + lokal.slice(b);
    aendern(neu);
    requestAnimationFrame(() => {
      el.focus();
      const p = b + vor.length + (a === b ? 0 : nach.length);
      el.setSelectionRange(p, p);
    });
  };

  const oeffne = (punkte: MenuePunkt[]) => (e: React.MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setMenue({ x: r.left, y: r.bottom + 4, punkte });
  };

  const farben: MenuePunkt[] = [
    ...Object.entries(rollen).map(([name, hex]) => ({ text: name, symbol: <span className="farbfleck" style={{ background: hex }} />, aktion: () => einsetzen(`<${name}>`, `</${name}>`) })),
    ...(Object.keys(rollen).length ? (["trenner"] as const) : []),
    ...LEGACY_FARBEN.map((f) => ({ text: f.name, symbol: <span className="farbfleck" style={{ background: f.hex }} />, aktion: () => einsetzen(`<${f.name}>`, `</${f.name}>`) })),
  ];

  const vorschlaege = (filter: string): MenuePunkt[] =>
    platzhalter
      .filter((p) => p.name.startsWith(filter))
      .map((p) => ({ text: `{${p.name}${p.mehrzeilig ? "..." : ""}}`, aktion: () => einsetzen(`${p.name.slice(filter.length)}${p.mehrzeilig ? "..." : ""}}`) }));

  const taste = (e: KeyboardEvent) => {
    if (e.key === "{" && platzhalter.length) {
      const el = feld.current!;
      const r = el.getBoundingClientRect();
      window.setTimeout(() => setMenue({ x: r.left, y: r.bottom + 4, punkte: vorschlaege("") }), 0);
    }
    if (!mehrzeilig && e.key === "Enter") absenden(lokal);
  };

  const befunde = useMemo(() => lokal.split("\n").flatMap((z) => zerlege(z, rollen).befunde), [lokal, rollen]);

  const Eingabe = mehrzeilig ? "textarea" : "input";
  return (
    <div className="feld textfeld">
      <div className="textfeld-kopf">
        <label htmlFor={id}>{label}</label>
        {!deaktiviert && (
          <div className="textfeld-werkzeuge" role="toolbar" aria-label={`Formatierung für ${label}`}>
            <button type="button" className="mini-knopf" title="Farbe" aria-label="Farbe" onClick={oeffne(farben)}>
              <Palette size={13} aria-hidden="true" />
            </button>
            <button type="button" className="mini-knopf" title="Fett" aria-label="Fett" onClick={() => einsetzen("<b>", "</b>")}>
              <Bold size={13} aria-hidden="true" />
            </button>
            <button type="button" className="mini-knopf" title="Kursiv" aria-label="Kursiv" onClick={() => einsetzen("<i>", "</i>")}>
              <Italic size={13} aria-hidden="true" />
            </button>
            <button type="button" className="mini-knopf" title="Unterstrichen" aria-label="Unterstrichen" onClick={() => einsetzen("<u>", "</u>")}>
              <Underline size={13} aria-hidden="true" />
            </button>
            <button type="button" className="mini-knopf" title="Durchgestrichen" aria-label="Durchgestrichen" onClick={() => einsetzen("<st>", "</st>")}>
              <Strikethrough size={13} aria-hidden="true" />
            </button>
            {platzhalter.length > 0 && (
              <button type="button" className="mini-knopf" title="Platzhalter einsetzen" aria-label="Platzhalter einsetzen" onClick={oeffne(vorschlaege("").map((p) => (p === "trenner" ? p : { ...p, aktion: () => einsetzen(`${p.text}`) })))}>
                <Braces size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
      <Eingabe
        id={id}
        ref={feld}
        className="eingabe mono-leicht"
        value={lokal}
        placeholder={leer}
        disabled={deaktiviert}
        spellCheck={false}
        rows={mehrzeilig ? Math.min(8, Math.max(3, lokal.split("\n").length + 1)) : undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => aendern(e.target.value)}
        onBlur={() => {
          if (lokal === zuletzt.current && wert !== lokal) {
            setLokal(wert);
            zuletzt.current = wert;
          } else absenden(lokal);
        }}
        onKeyDown={taste}
        aria-describedby={befunde.length ? `${id}-befund` : undefined}
      />
      {vorschau && lokal.trim() !== "" && (
        <div className={`textfeld-vorschau vorschau-${vorschau}`} aria-hidden="true">
          <SpielText text={lokal} rollen={rollen} glyphen={glyphen} skala={1} farbe={vorschau === "titel" ? "#404040" : vorschau === "lore" ? "#AA00AA" : "#FFFFFF"} schatten={vorschau !== "titel"} kursivStandard={kursivStandard} />
        </div>
      )}
      {befunde.map((b, i) => (
        <span key={i} id={i === 0 ? `${id}-befund` : undefined} className="feld-warnung">
          {b.meldung}
        </span>
      ))}
      {hinweis && <span className="feld-hinweis">{hinweis}</span>}
      {menue && <MenueListe punkte={menue.punkte} x={menue.x} y={menue.y} label="Einsetzen" onSchliessen={() => setMenue(null)} />}
    </div>
  );
}
